import { ErrorBoundary } from "ErrorBoundary"
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react"
import { Button, Card, Spinner } from "react-bootstrap"
import { useSelector } from "react-redux"
import { RootState } from "store"

import { usePrevetGetSensitivityDataForSegment } from "../hooks/usePrevetGetSensitivityDataForSegment"
import { usePrevetGetVessels } from "../hooks/usePrevetGetVessels"

import { VesselSegment } from "user/userService"
import { useFetchVesselDetails } from "../hooks/useFetchVesselDetails"
import { VesselStay, useFetchVesselStays } from "../hooks/useFetchVesselStays"
import { usePrevetGetRightShipData } from "../hooks/usePrevetGetRightShipData"
import { PrevetLastFiveCargoes } from "./PrevetLast5Cargoes"
import { PrevetPortStays } from "./PrevetPortStays"
import { PrevetRightShip } from "./PrevetRightShip"
import { getAvgSpeedFromPortStays } from "./PrevetPerformanceExposureCard"
import { PrevetVesselHistory } from "./PrevetVesselHistory/PrevetVesselHistory"
import { ChosenVesselForSelect, PrevetVesselSearch } from "./PrevetVesselSearch"
import { PrevetVesselSpeedWrapper } from "./PrevetVesselSpeedWrapper"
import { PrevetVesselTradingModal } from "./PrevetVesselTradingModal"

import { PrevetSpeedGraphZoneType } from "../getSpeedChartOptionsHelpers"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import {
  DEFAULT_SPEED_DATE_RANGE,
  SortOrder,
} from "../hooks/prevetStateReducer"
import { PrevetDDWidget } from "./PrevetDDWidget"
import { PrevetPerformanceExposureCard } from "./PrevetPerformanceExposureCard"
import PrevetPortAlertWidget from "./PrevetPortAlertWidget"
import PrevetProlongedStayWidget from "./PrevetProlongedStayWidget"
import PrevetSearchWidget from "./PrevetSearchWidget"
import PrevetVesselInfoCard from "./PrevetVesselInfoCard"
import { VesselDetails } from "../hooks/usePrevetAPI"
import PrevetModalNewVesselNotice from "./PrevetModalNewVesselNotice"
import { getAge } from "./PrevetVesselInfoCard"

export type ImoNumber = string
export interface VesselListSingle {
  label: string
  value: ImoNumber
}

export interface VesselSegmentInputSelection {
  label: string
  value: VesselSegment
}

export interface SelectOption {
  label: string
  value: string
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div>
      <p>Sorry, we are having technical issues, please contact support.</p>
      <pre>{error.message}</pre>
    </div>
  )
}

interface PrevetMainProps {
  selectedIndex: number
  setVesselDetails: Dispatch<SetStateAction<VesselDetails | null>>
}

export function PrevetMain({
  selectedIndex,
  setVesselDetails,
}: PrevetMainProps) {
  const { getTabState, getTabStateValue, setTabStateValue } =
    usePrevetTabStateContext()

  const resolveAction = <T,>(action: SetStateAction<T>, current: T): T =>
    typeof action === 'function' ? (action as (prev: T) => T)(current) : action

  const defaultSeaDays = 25
  const standardVesselDefaultSpeed = 12

  const describedLadenSpeed = (getTabStateValue("cvdLadenSpeed", undefined) as string) ?? String(standardVesselDefaultSpeed)
  const setDescribedLadenSpeed: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("cvdLadenSpeed", resolveAction(action, describedLadenSpeed), undefined)

  const describedBallastSpeed = (getTabStateValue("cvdBallastSpeed", undefined) as string) ?? String(standardVesselDefaultSpeed)
  const setDescribedBallastSpeed: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("cvdBallastSpeed", resolveAction(action, describedBallastSpeed), undefined)

  const ladenSeaDays = (getTabStateValue("cvdLadenSeaDays", undefined) as string) ?? String(defaultSeaDays)
  const setLadenSeaDays: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("cvdLadenSeaDays", resolveAction(action, ladenSeaDays), undefined)

  const ballastSeaDays = (getTabStateValue("cvdBallastSeaDays", undefined) as string) ?? String(defaultSeaDays)
  const setBallastSeaDays: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("cvdBallastSeaDays", resolveAction(action, ballastSeaDays), undefined)

  const ladenFuelCon = (getTabStateValue("cvdLadenFuelCon", undefined) as string) || ""
  const setLadenFuelCon: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("cvdLadenFuelCon", resolveAction(action, ladenFuelCon), undefined)

  const ballastFuelCon = (getTabStateValue("cvdBallastFuelCon", undefined) as string) || ""
  const setBallastFuelCon: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("cvdBallastFuelCon", resolveAction(action, ballastFuelCon), undefined)

  const [initBallastSpeed, setInitBallastSpeed] = useState<string>(describedBallastSpeed)
  const [initLadenSpeed, setInitLadenSpeed] = useState<string>(describedLadenSpeed)

  const user = useSelector((state: RootState) => state.user)
  const vesselSegments = user.chosenOrganization?.segments

  const [isDescribedSpeedChanged, setIsDescribedSpeedChanged] = useState<boolean>(false)

  const prevetSpeedGraphZoneType = (getTabStateValue("vesselSpeedGraphZoneType", undefined) as PrevetSpeedGraphZoneType | undefined) ?? PrevetSpeedGraphZoneType.Outliers
  const setPrevetSpeedGraphZoneType: Dispatch<SetStateAction<PrevetSpeedGraphZoneType | undefined>> = (action) => {
    const value = resolveAction(action, prevetSpeedGraphZoneType)
    setTabStateValue("vesselSpeedGraphZoneType", value ?? PrevetSpeedGraphZoneType.Outliers, undefined)
  }

  const vesselSpeedDateRange = (getTabStateValue("vesselSpeedDateRange", undefined) as string) || DEFAULT_SPEED_DATE_RANGE
  const setVesselSpeedDateRange: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("vesselSpeedDateRange", resolveAction(action, vesselSpeedDateRange), undefined)

  const vesselState = {
    includeBallast: (getTabStateValue("hvIncludeBallast", undefined) as boolean) ?? true,
    includeLaden: (getTabStateValue("hvIncludeLaden", undefined) as boolean) ?? true,
  }
  const setVesselState = (action: SetStateAction<{ includeBallast: boolean; includeLaden: boolean }>) => {
    const newState = typeof action === 'function' ? action(vesselState) : action
    setTabStateValue("hvIncludeBallast", newState.includeBallast, undefined)
    setTabStateValue("hvIncludeLaden", newState.includeLaden, undefined)
  }
  const defaultSegment = user.defaultSegment

  const [chosenSegment, setChosenSegment] =
    useState<VesselSegmentInputSelection | null>(
      defaultSegment ? { value: defaultSegment, label: defaultSegment } : null
    )

  useEffect(() => {
    if (chosenSegment != null) {
      const tabState = getTabState(selectedIndex)
      if (!tabState?.cvdLadenFuelCon && !tabState?.cvdBallastFuelCon) {
        setLadenFuelCon(String(getFuelConBySegment(chosenSegment)))
        setBallastFuelCon(String(getFuelConBySegment(chosenSegment)))
      }
    }
  }, [chosenSegment])

  const { vesselsData, getVesselsLoading } = usePrevetGetVessels({
    vesselSegments,
  })


  /**
   * Speed Data
   * VesselLegs data is fetched from PrevetVesselSpeedChart
   */
  const [ladenAvgSpeed, setLadenAvgSpeed] = useState<number | undefined>()
  const [ballastAvgSpeed, setBallastAvgSpeed] = useState<number | undefined>()
  const [ladenMaxSpeed, setLadenMaxSpeed] = useState<number | undefined>()
  const [ballastMaxSpeed, setBallastMaxSpeed] = useState<number | undefined>()
  const [userLadenVesselSpeed, setUserLadenVesselSpeed] = useState<
    string | undefined
  >()
  const [userBallastVesselSpeed, setUserBallastVesselSpeed] = useState<
    string | undefined
  >()

  const isShowSeaMarginChecked = (getTabStateValue("showSeaMargin", undefined) as boolean) ?? true
  const setIsShowSeaMarginChecked: Dispatch<SetStateAction<boolean>> = (action) => setTabStateValue("showSeaMargin", resolveAction(action, isShowSeaMarginChecked), undefined)
  const [ladenAvgMargin, setLadenAvgMargin] = useState<number | undefined>(
    undefined
  )
  const [ballastAvgMargin, setBallastAvgMargin] = useState<number | undefined>(
    undefined
  )

  const ladenMarginOverride = getTabStateValue(
    "ladenMarginOverride",
    undefined
  ) as number | undefined
  const ballastMarginOverride = getTabStateValue(
    "ballastMarginOverride",
    undefined
  ) as number | undefined
  const effectiveLadenMargin = ladenMarginOverride ?? ladenAvgMargin
  const effectiveBallastMargin = ballastMarginOverride ?? ballastAvgMargin

  /**
   * Sensitivity
   */
  const { getSegmentSensitivityData, segmentSensitivityData } =
    usePrevetGetSensitivityDataForSegment()

  const ladenChartInstance = useRef<any>()
  const ballastChartInstance = useRef<any>()

  /**
   * Trading Map
   */
  const [isTradingModalShown, setIsTradingModalShown] = useState(false)
  const [dateForCenterMarker, setDateForCenterMarker] = useState<
    string | undefined
  >()
  const [chartLadenStatus, setChartLadenStatus] = useState<boolean | undefined>()

  /* Chosen vessel */
  const chosenVessel = getTabStateValue("imo", undefined) as SelectOption | undefined
  const setChosenVessel: Dispatch<SetStateAction<SelectOption | undefined>> = (action) => setTabStateValue("imo", resolveAction(action, chosenVessel) as any, undefined)

  const {
    vesselDetailsData,
    clearVesselDetailsData,
    fetchVesselDetails,
    getVesselDetailsLoading,
    getVesselDetailsError,
  } = useFetchVesselDetails()

  const { vesselStaysData, isVesselStaysLoading, isVesselStaysError } =
    useFetchVesselStays({
      imo: vesselDetailsData?.imo,
      built: vesselDetailsData?.built,
    })
  const portStaysLadenAvgSpeed = getAvgSpeedFromPortStays({
    vesselStaysData,
    isLaden: true,
  })
  const portStaysBallastAvgSpeed = getAvgSpeedFromPortStays({
    vesselStaysData,
    isLaden: false,
  })

  const selectedDate = (getTabStateValue("date", undefined) as string) || new Date().toISOString().split("T")[0]
  const setSelectedDate: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("date", resolveAction(action, selectedDate), undefined)

  const filteredVesselStaysData = useMemo(() => {
    if (!vesselStaysData || !selectedDate) return undefined
    return vesselStaysData.filter(
      (stay) => new Date(stay.date_to) <= new Date(selectedDate)
    )
  }, [vesselStaysData, selectedDate])

  /* modal for newly built vessels */
  const [openNoticeNewVesselModal, setOpenNewVesselModal] =
    useState<boolean>(false)

  //set ballast and laden speed for chart whenever it re-renders
  useEffect(() => {
    setInitBallastSpeed(describedBallastSpeed)
    setInitLadenSpeed(describedLadenSpeed)
  }, [setBallastAvgSpeed, setLadenAvgSpeed, ballastAvgSpeed, ladenAvgSpeed])

  useEffect(() => {
    if (vesselDetailsData) {
      setVesselDetails(vesselDetailsData)
    }
  }, [vesselDetailsData])

  //setchosensegment
  useEffect(() => {
    if (chosenVessel) {
      const tempSegment = chosenVessel?.label
        .split("-")
        .at(-1)
        ?.replace(/[^a-zA-Z]/g, "")
        .toLowerCase()
      if (tempSegment && tempSegment !== chosenSegment?.value) {
        setChosenSegment({
          value: tempSegment as VesselSegment,
          label: tempSegment as VesselSegment,
        })
      }
      if (tempSegment) {
        getSegmentSensitivityData(tempSegment, selectedDate)
      }
      setIsDescribedSpeedChanged(false)
    }
  }, [chosenVessel, selectedDate])

  useEffect(() => {
    if (segmentSensitivityData) {
      if (!hirePrice) setHirePrice(String(segmentSensitivityData.benchmark.spot_price))
      if (!fuelPrice) setFuelPrice(String(segmentSensitivityData.benchmark.fuel_price))
    }
  }, [segmentSensitivityData])

  /* Date integration */

  const hirePrice = (getTabStateValue("perfExposureHireRate", undefined) as string) || ""
  const setHirePrice: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("perfExposureHireRate", resolveAction(action, hirePrice), undefined)

  const fuelPrice = (getTabStateValue("perfExposureFuelPrice", undefined) as string) || ""
  const setFuelPrice: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("perfExposureFuelPrice", resolveAction(action, fuelPrice), undefined)

  const filterText = (getTabStateValue("hvSearchText", undefined) as string) || ""
  const setFilterText: Dispatch<SetStateAction<string>> = (action) => setTabStateValue("hvSearchText", resolveAction(action, filterText), undefined)

  const currentPage = (getTabStateValue("hvCurrentPage", undefined) as number) || 1
  const setCurrentPage: Dispatch<SetStateAction<number>> = (action) => setTabStateValue("hvCurrentPage", resolveAction(action, currentPage), undefined)

  const rowsPerPage = (getTabStateValue("hvRowsPerPage", undefined) as number) || 10
  const setRowsPerPage: Dispatch<SetStateAction<number>> = (action) => setTabStateValue("hvRowsPerPage", resolveAction(action, rowsPerPage), undefined)

  const sortState = {
    prop: (getTabStateValue("hvSortByProp", undefined) as string) || "date_from",
    order: ((getTabStateValue("hvSortByOrder", undefined) as SortOrder) || "desc"),
  }
  const setSortState = (action: SetStateAction<{ prop: string; order: SortOrder }>) => {
    const newState = typeof action === 'function' ? action(sortState) : action
    setTabStateValue("hvSortByProp", newState.prop, undefined)
    setTabStateValue("hvSortByOrder", newState.order, undefined)
  }

  const clearData = () => {
    clearVesselDetailsData()
    setBallastAvgSpeed(undefined)
    setLadenAvgSpeed(undefined)
    setBallastMaxSpeed(undefined)
    setLadenMaxSpeed(undefined)
  }

  useEffect(() => {
    if (chosenVessel) {
      fetchVesselDetails(+chosenVessel.value)
    } else {
      clearVesselDetailsData()
    }
  }, [chosenVessel?.value])

  /**
   * Right Ship
   */
  const { rightShipData, rightShipDataLoading } = usePrevetGetRightShipData({
    imo: Number(vesselDetailsData?.imo),
    date: selectedDate,
  })
  const rightShipVesselInfo = rightShipData?.items[0]

  const usePsCollapsed = () => {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

    useEffect(() => {
      // Function to check if .ps-collapsed exists in the document
      const checkCollapsed = () => {
        setIsCollapsed(!!document.querySelector(".ps-collapsed"))
      }

      // Observe mutations in the document
      const observer = new MutationObserver(() => checkCollapsed())

      observer.observe(document.body, {
        attributes: true,
        subtree: true,
        childList: true,
      })

      // Initial check
      checkCollapsed()

      return () => {
        observer.disconnect() // Clean up observer on unmount
      }
    }, [])

    return isCollapsed
  }

  const isCollapsed = usePsCollapsed()

  // Track window width to detect “mobile”
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const mainContentRef = useRef<string | null>(null)

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    const mainContentEl = document.querySelector(".main-content") as HTMLElement
    if (!mainContentEl) return

    // Store the original width if not already stored
    if (!mainContentRef.current) {
      mainContentRef.current = mainContentEl.style.width
    }

    const isMobile = windowWidth < 768
    if (isMobile) {
      // Force 100vw if mobile
      mainContentEl.style.width = "100vw"
    } else {
      // If desktop, decide width based on collapsed or not
      mainContentEl.style.width = isCollapsed
        ? "calc(100vw - 90px)"
        : "calc(100vw - 260px)"
    }

    // Cleanup function to restore the original width
    return () => {
      if (mainContentRef.current) {
        mainContentEl.style.width = mainContentRef.current
      }
    }
  }, [windowWidth, isCollapsed])

  // show the notice iff the currently selected vessel is "new"
  useEffect(() => {
    let shouldShow = false

    if (rightShipVesselInfo) {
      const age = getAge((rightShipVesselInfo as any).dateOfBuild)
      shouldShow = age.years < 1 && age.months < 6
    } else if (vesselDetailsData?.built) {
      const buildYear = Number(vesselDetailsData.built)
      const buildDate = new Date(buildYear, 0, 1)
      const cutoff = new Date()
      cutoff.setFullYear(cutoff.getFullYear() - 1)
      shouldShow = buildDate > cutoff
    }

    setOpenNewVesselModal(shouldShow)
  }, [rightShipVesselInfo, vesselDetailsData?.built])

  return (
    <>
      {/* Modal for newly built vessel notice */}
      <PrevetModalNewVesselNotice
        show={openNoticeNewVesselModal}
        setShowModal={setOpenNewVesselModal}
      />
      <div className="PrevetMainContainer">
        {filteredVesselStaysData &&
          vesselDetailsData &&
          isTradingModalShown &&
          chosenSegment && (
            <PrevetVesselTradingModal
              chosenSegment={chosenSegment}
              closeTradingModal={() => {
                setIsTradingModalShown(false)
                setDateForCenterMarker(undefined)
                setChartLadenStatus(undefined)
              }}
              isTradingModalShown={isTradingModalShown}
              vesselDetails={vesselDetailsData}
              vesselStays={filteredVesselStaysData}
              dateForCenterMarker={dateForCenterMarker}
              chartLadenStatus={chartLadenStatus}
            />
          )}
        {!vesselDetailsData && !getVesselDetailsError && (
          <Card className="PrevetMain_SearchBar">
            <Card.Body>
              <div className="VesselSearch_Row">
                <h5 className="mb-0 me-3">Search Vessel</h5>
                {defaultSegment && chosenSegment && vesselSegments && (
                  <PrevetVesselSearch
                    vesselsData={vesselsData}
                    getVesselsLoading={getVesselsLoading}
                    getVesselDetailsLoading={getVesselDetailsLoading}
                    getChosenVessel={({ imo }: ChosenVesselForSelect) => {
                      fetchVesselDetails(imo)
                      setUserLadenVesselSpeed(undefined)
                      setUserBallastVesselSpeed(undefined)
                      setTabStateValue("ladenMarginOverride", undefined, undefined)
                      setTabStateValue("ballastMarginOverride", undefined, undefined)
                    }}
                    clearChosenVesselData={() => {
                      clearData()
                    }}
                    chosenVessel={chosenVessel}
                    setChosenVessel={setChosenVessel}
                    showRecentSearch={true}
                  />
                )}
                <div className="VesselSearch_ButtonContainer">
                  <Button
                    color="primary"
                    className="btn btn-primary w-100 waves-effect waves-light"
                    disabled={!vesselDetailsData || !filteredVesselStaysData}
                    onClick={() => {
                      setIsTradingModalShown(true)
                    }}
                  >
                    {getVesselDetailsLoading || getVesselsLoading ? (
                      <Spinner
                        size="sm"
                        variant="light"
                        animation="border"
                        role="status"
                      />
                    ) : (
                      <div>
                        <span className="me-2">
                          <i className="ti-eye" />
                        </span>
                        Info & Trading
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          {!vesselDetailsData && !getVesselDetailsError && (
            <div
              role="alert"
              className="fade bg-ifp mb-0 alert alert-info show"
            >
              Please select a vessel
            </div>
          )}
          {getVesselDetailsError && (
            <div
              role="alert"
              className="fade bg-ifp mb-0 alert alert-info show"
            >
              Unable to get vessel, please contact support.
            </div>
          )}
          {vesselDetailsData && chosenSegment && vesselSegments && (
            <>
              <div className="PrevetContainer">
                {/* Selected vessel card */}
                <PrevetSearchWidget
                  ladenFuelCon={ladenFuelCon}
                  ladenSeaDays={ladenSeaDays}
                  describedLadenSpeed={describedLadenSpeed}
                  describedBallastSpeed={describedBallastSpeed}
                  ballastFuelCon={ballastFuelCon}
                  ballastSeaDays={ballastSeaDays}
                  setLadenFuelCon={setLadenFuelCon}
                  setLadenSeaDays={setLadenSeaDays}
                  setDescribedLadenSpeed={setDescribedLadenSpeed}
                  setDescribedBallastSpeed={setDescribedBallastSpeed}
                  setBallastFuelCon={setBallastFuelCon}
                  setBallastSeaDays={setBallastSeaDays}
                  ladenChartInstance={ladenChartInstance}
                  ballastChartInstance={ballastChartInstance}
                  speedGraphLadenAvgSpeed={ladenAvgSpeed}
                  speedGraphBallastAvgSpeed={ballastAvgSpeed}
                  portStaysLadenAvgSpeed={portStaysLadenAvgSpeed}
                  portStaysBallastAvgSpeed={portStaysBallastAvgSpeed}
                  realLadenMaxSpeed={ladenMaxSpeed}
                  realBallastMaxSpeed={ballastMaxSpeed}
                  segmentSensitivityData={segmentSensitivityData}
                  setUserLadenVesselSpeed={(speed: string | undefined) => {
                    setUserLadenVesselSpeed(speed)
                  }}
                  setUserBallastVesselSpeed={(speed: string | undefined) => {
                    setUserBallastVesselSpeed(speed)
                  }}
                  vesselsData={vesselsData}
                  getVesselsLoading={getVesselsLoading}
                  getVesselDetailsLoading={getVesselDetailsLoading}
                  chosenSegment={chosenSegment}
                  getChosenVessel={({ imo }: ChosenVesselForSelect) => {
                    fetchVesselDetails(imo)
                    setUserLadenVesselSpeed(undefined)
                    setUserBallastVesselSpeed(undefined)
                    setTabStateValue("ladenMarginOverride", undefined, undefined)
                    setTabStateValue("ballastMarginOverride", undefined, undefined)
                  }}
                  setChosenSegment={(segment: VesselSegmentInputSelection) => {
                    setChosenSegment(segment)
                  }}
                  vesselSegments={vesselSegments.map((segment) => {
                    return { label: segment, value: segment }
                  })}
                  clearChosenVesselData={() => {
                    clearData()
                  }}
                  chosenVessel={chosenVessel}
                  setChosenVessel={setChosenVessel}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  showTradingModal={() => setIsTradingModalShown(true)}
                  describedFuelPrice={Number(fuelPrice)}
                  describedHirePrice={Number(hirePrice)}
                  setInitBallastSpeed={setInitBallastSpeed}
                  setInitLadenSpeed={setInitLadenSpeed}
                  isShowSeaMarginChecked={isShowSeaMarginChecked}
                  ladenSM={effectiveLadenMargin}
                  ballastSM={effectiveBallastMargin}
                  setIsDescribedSpeedChanged={setIsDescribedSpeedChanged}
                />
                {/* Prolonged Stay Card */}
                {isVesselStaysLoading ? (
                  <Card className="PrevetContainer_ProlongedStayWidget">
                    <div className="d-flex flex-column h-100 w-100 align-items-center justify-content-center">
                      <div className="spinner-border mb-2" role="status">
                        <span className="sr-only"></span>
                      </div>
                      Loading Prolonged Stay Widget...
                    </div>
                  </Card>
                ) : (
                  <PrevetProlongedStayWidget
                    vesselStaysData={filteredVesselStaysData}
                    isVesselStaysLoading={isVesselStaysLoading}
                    isVesselStaysError={isVesselStaysError}
                  />
                )}
                {/* Dry Dock Card */}
                <Card className="PrevetContainer_DDWidget">
                  {isVesselStaysLoading && (
                    <div className="d-flex flex-column h-100 w-100 align-items-center justify-content-center">
                      <div className="spinner-border mb-2" role="status">
                        <span className="sr-only"></span>
                      </div>
                      Loading Dry Dock - IWS - Repairs Widget...
                    </div>
                  )}

                  {filteredVesselStaysData && !isVesselStaysLoading ? (
                    <PrevetDDWidget
                      vesselDetails={vesselDetailsData}
                      vesselStays={filteredVesselStaysData}
                    />
                  ) : (
                    isVesselStaysError && (
                      <div className="d-flex flex-column h-100 w-100 align-items-center justify-content-center">
                        Error loading vessel stays
                      </div>
                    )
                  )}
                </Card>
                {/* Collapsible div */}
                {/* Last 5 Cargoes card */}
                <Card className="PrevetContainer_FiveCargoes">
                  {isVesselStaysLoading && (
                    <div className="d-flex flex-column h-100 w-100 align-items-center justify-content-center">
                      <div className="spinner-border mb-2" role="status">
                        <span className="sr-only"></span>
                      </div>
                      Loading Last 5 Cargoes...
                    </div>
                  )}
                  {filteredVesselStaysData && !isVesselStaysLoading ? (
                    <Card.Body>
                      <PrevetLastFiveCargoes
                        vesselStaysData={filteredVesselStaysData}
                      />
                    </Card.Body>
                  ) : (
                    isVesselStaysError && (
                      <div className="d-flex w-100 h-100 align-items-center justify-content-center">
                        Error loading vessel stays
                      </div>
                    )
                  )}
                </Card>
                {/* Port Alerts Card */}
                <PrevetPortAlertWidget vesselStays={filteredVesselStaysData} />
                {/* Rightship Card */}
                {rightShipDataLoading && (
                  <Card className="PrevetContainer_RightShip d-flex align-items-center justify-content-center">
                    <Spinner animation="border" role="status" />
                    <div className="mt-4">Loading RightShip Data...</div>
                  </Card>
                )}
                {rightShipVesselInfo && (
                  <PrevetRightShip
                    rightShipVesselInfo={rightShipVesselInfo}
                    dateSelected={selectedDate}
                  />
                )}
                {/* Vessel Speed Chart */}
                <Card className="PrevetContainer_SpeedGraph h-100">
                  <PrevetVesselSpeedWrapper
                    vesselName={vesselDetailsData.vessel_name}
                    vesselImo={vesselDetailsData.imo}
                    setIsTradingModalShown={setIsTradingModalShown}
                    setDateForCenterMarker={setDateForCenterMarker}
                    setChartLadenStatus={setChartLadenStatus}
                    setLadenAvgSpeed={setLadenAvgSpeed}
                    setBallastAvgSpeed={setBallastAvgSpeed}
                    setLadenMaxSpeed={setLadenMaxSpeed}
                    setBallastMaxSpeed={setBallastMaxSpeed}
                    ladenChartInstance={ladenChartInstance}
                    ballastChartInstance={ballastChartInstance}
                    userLadenVesselSpeed={userLadenVesselSpeed}
                    userBallastVesselSpeed={userBallastVesselSpeed}
                    endDate={selectedDate}
                    setPrevetSpeedGraphZoneType={setPrevetSpeedGraphZoneType}
                    prevetSpeedGraphZoneType={prevetSpeedGraphZoneType}
                    setVesselSpeedDateRange={setVesselSpeedDateRange}
                    vesselSpeedDateRange={vesselSpeedDateRange}
                    describedBallastSpeed={describedBallastSpeed}
                    describedLadenSpeed={describedLadenSpeed}
                    chosenSegment={chosenSegment}
                    isShowSeaMarginChecked={isShowSeaMarginChecked}
                    setIsShowSeaMarginChecked={setIsShowSeaMarginChecked}
                    setBallastAvgMargin={setBallastAvgMargin}
                    setLadenAvgMargin={setLadenAvgMargin}
                  />
                </Card>
                {/* Port stays */}
                <PrevetPortStays
                  vesselDetailsData={vesselDetailsData}
                  vesselStaysData={filteredVesselStaysData}
                  isVesselStaysLoading={isVesselStaysLoading}
                  isVesselStaysError={isVesselStaysError}
                />
                {/* Performance Exposure card */}
                <PrevetPerformanceExposureCard
                  ladenFuelCon={ladenFuelCon}
                  ballastFuelCon={ballastFuelCon}
                  describedBallastSpeed={describedBallastSpeed}
                  describedLadenSpeed={describedLadenSpeed}
                  ladenSeaDays={ladenSeaDays}
                  ballastSeaDays={ballastSeaDays}
                  ladenChartInstance={ladenChartInstance}
                  ballastChartInstance={ballastChartInstance}
                  speedGraphLadenAvgSpeed={ladenAvgSpeed}
                  speedGraphBallastAvgSpeed={ballastAvgSpeed}
                  portStaysLadenAvgSpeed={portStaysLadenAvgSpeed}
                  portStaysBallastAvgSpeed={portStaysBallastAvgSpeed}
                  realLadenMaxSpeed={ladenMaxSpeed}
                  realBallastMaxSpeed={ballastMaxSpeed}
                  segmentSensitivityData={segmentSensitivityData}
                  vesselName={vesselDetailsData.vessel_name}
                  defaultFuelCon={getFuelConBySegment(chosenSegment)}
                  setUserLadenVesselSpeed={(speed: string | undefined) => {
                    setUserLadenVesselSpeed(speed)
                  }}
                  setUserBallastVesselSpeed={(speed: string | undefined) => {
                    setUserBallastVesselSpeed(speed)
                  }}
                  setHirePrice={setHirePrice}
                  hirePrice={hirePrice}
                  setFuelPrice={setFuelPrice}
                  fuelPrice={fuelPrice}
                  selectedDate={selectedDate}
                  vesselImo={vesselDetailsData.imo.toString()}
                  chosenSegment={chosenSegment}
                  isShowSeaMarginChecked={isShowSeaMarginChecked}
                  ladenAvgMargin={effectiveLadenMargin}
                  ballastAvgMargin={effectiveBallastMargin}
                  isDescribedSpeedChanged = {isDescribedSpeedChanged}
                />
                {/* Vessel Info Card */}
                <Card className="PrevetContainer_VesselInfo w-100 h-100">
                  {getVesselDetailsLoading || rightShipDataLoading ? (
                    <div className="d-flex flex-column w-100 h-100 justify-content-center align-items-center text-muted">
                      <Spinner
                        className="mb-3"
                        animation="border"
                        role="status"
                      />
                      Loading vessel details...
                    </div>
                  ) : (
                    vesselDetailsData &&
                    rightShipVesselInfo && (
                      <PrevetVesselInfoCard
                        vesselDetails={vesselDetailsData}
                        dateOfBuild={(rightShipVesselInfo as any).dateOfBuild}
                      />
                    )
                  )}
                </Card>
              </div>
              <div className="PrevetChosenVesselData_Row mt-3">
                <Card>
                  <Card.Body>
                    {isVesselStaysLoading && (
                      <div className="d-flex flex-column align-items-center">
                        <Spinner
                          className="mb-3"
                          animation="border"
                          role="status"
                        />
                        <div>Loading historical vessel data...</div>
                      </div>
                    )}
                    {isVesselStaysError && (
                      <div className="d-flex justify-content-center">
                        Error loading historical vessel data.
                      </div>
                    )}
                    {filteredVesselStaysData && (
                      <PrevetVesselHistory
                        vesselStays={filteredVesselStaysData}
                        setVesselState={setVesselState}
                        vesselState={vesselState}
                        filterText={filterText}
                        setFilterText={setFilterText}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        setSortState={setSortState}
                        sortState={sortState}
                        chosenSegment={chosenSegment}
                      />
                    )}
                  </Card.Body>
                </Card>
                {/* <TestCharts /> */}
              </div>
            </>
          )}
        </ErrorBoundary>
      </div>
    </>
  )
}

export function getFuelConBySegment(segment: VesselSegmentInputSelection) {
  if (segment.value === VesselSegment.Handysize) {
    return 18
  }
  if (segment.value === VesselSegment.Supramax) {
    return 23
  }
  if (segment.value === VesselSegment.Panamax) {
    return 25
  }
  // Otherwise chosen segment is capesize
  return 43
}
